import { useEffect, useRef, useState } from 'react';
import { useAnimatedNumber } from '../hooks/useAnimatedNumber.js';
import { useAnchor } from '../onboarding/useAnchor.js';
import { useGameStore } from '../stores/game.js';
import { formatNumber } from '../utils/format.js';
import styles from './AwarenessCounter.module.css';

/**
 * Пороги-милестоуны на счётчике осознаний. При пересечении каждого —
 * короткий gold pulse + chime feel. Doc 28 §C — без celebration игрок
 * не замечает прогресс на «круглых» цифрах.
 */
const AWARENESS_THRESHOLDS = [100, 1000, 10000, 100000, 1_000_000] as const;
const SPIRIT_THRESHOLDS = [1, 10, 50, 200] as const;

/** Найти ближайший только что пересечённый порог из списка (или null). */
function findCrossedThreshold(
  prev: number,
  next: number,
  thresholds: readonly number[],
): number | null {
  if (next <= prev) return null;
  for (const t of thresholds) {
    if (prev < t && next >= t) return t;
  }
  return null;
}

export function AwarenessCounter(): JSX.Element {
  const awarenesses = useGameStore((s) => s.state.resources.awarenesses);
  const spiritBalls = useGameStore((s) => s.state.resources.spirit_balls);
  // 2026-05-26 polish: count-up animation для крупных скачков (типа
  // кристаллизация +N духобаллов сразу). Threshold 5 — обычный +1/+2 на
  // тапе не дёргает анимацию, kristallization-level крупные дельты —
  // плавно докручиваются за 700ms. См. useAnimatedNumber.
  const animatedAwarenesses = useAnimatedNumber(awarenesses, {
    duration: 700,
    instantThreshold: 5,
  });
  // Spirit balls обычно прибавляются крупными скачками (1, 5, 10...)
  // — порог ниже, любое изменение анимируется.
  const animatedSpiritBalls = useAnimatedNumber(spiritBalls, {
    duration: 800,
    instantThreshold: 1,
  });
  // Регистрируем как onboarding anchor — tip-bubble «копится здесь».
  const rootRef = useAnchor<HTMLDivElement>('counter');

  // Threshold pulse — short gold pulse при пересечении круглых цифр.
  const prevAwRef = useRef(awarenesses);
  const prevSbRef = useRef(spiritBalls);
  const [awPulse, setAwPulse] = useState(false);
  const [sbPulse, setSbPulse] = useState(false);

  useEffect(() => {
    const crossed = findCrossedThreshold(prevAwRef.current, awarenesses, AWARENESS_THRESHOLDS);
    prevAwRef.current = awarenesses;
    if (crossed === null) return;
    setAwPulse(true);
    const tid = window.setTimeout(() => {
      setAwPulse(false);
    }, 1200);
    return () => {
      window.clearTimeout(tid);
    };
  }, [awarenesses]);

  useEffect(() => {
    const crossed = findCrossedThreshold(prevSbRef.current, spiritBalls, SPIRIT_THRESHOLDS);
    prevSbRef.current = spiritBalls;
    if (crossed === null) return;
    setSbPulse(true);
    const tid = window.setTimeout(() => {
      setSbPulse(false);
    }, 1500);
    return () => {
      window.clearTimeout(tid);
    };
  }, [spiritBalls]);

  const awValueClass = `${styles.value ?? ''} ${awPulse ? (styles.valuePulse ?? '') : ''}`;
  const sbValueClass = `${styles.valueGold ?? ''} ${sbPulse ? (styles.valuePulse ?? '') : ''}`;

  return (
    <div ref={rootRef} className={styles.root}>
      <div className={styles.row}>
        <span className={awValueClass}>{formatNumber(animatedAwarenesses)}</span>
        <span className={styles.label}>осознаний</span>
      </div>
      {spiritBalls > 0 && (
        <div className={styles.row}>
          <span className={sbValueClass}>{formatNumber(animatedSpiritBalls)}</span>
          <span className={styles.label}>духобаллов</span>
        </div>
      )}
    </div>
  );
}
